import { appLegalConfig } from './legal/appLegalConfig'

export const LISTING_SHARE_BRAND = appLegalConfig.appName

export interface ListingShareInput {
  title: string
  price?: number | null
  manufacturer?: string | null
  modelName?: string | null
  conditionLabel?: string | null
  url: string
}

export function buildListingShareTitle(
  title: string,
  brand = LISTING_SHARE_BRAND,
  manufacturer?: string | null,
): string {
  const trimmed = normalizeListingShareTitle(title, manufacturer).trim()
  return trimmed ? `${brand} | ${trimmed}` : brand
}

/** Collapse duplicated manufacturer prefix — e.g. "Xiaomi Xiaomi 12" → "Xiaomi 12". */
export function normalizeListingShareTitle(title: string, manufacturer?: string | null): string {
  let trimmed = title.trim()
  const mfg = (manufacturer || '').trim()
  if (!trimmed || !mfg) return trimmed
  const escaped = mfg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return trimmed.replace(new RegExp(`^(${escaped}\\s+){2,}`, 'i'), `${mfg} `)
}

export function formatListingShareDevice(
  manufacturer?: string | null,
  modelName?: string | null,
): string {
  const mfg = (manufacturer || '').trim()
  let model = (modelName || '').trim()
  if (!model) return mfg
  if (mfg) model = normalizeListingShareTitle(model, mfg)
  if (!mfg) return model
  const mLower = mfg.toLowerCase()
  const modelLower = model.toLowerCase()
  if (modelLower === mLower || modelLower.startsWith(`${mLower} `)) return model
  return `${mfg} ${model}`
}

function shareTitleContainsDevice(title: string, device: string): boolean {
  const normalizedTitle = title.trim().toLowerCase()
  const normalizedDevice = device.trim().toLowerCase()
  if (!normalizedTitle || !normalizedDevice) return false
  return normalizedTitle.includes(normalizedDevice)
}

export function buildListingShareDescription(
  input: Pick<ListingShareInput, 'price' | 'manufacturer' | 'modelName' | 'conditionLabel' | 'title'>,
): string {
  const parts: string[] = []
  if (typeof input.price === 'number' && Number.isFinite(input.price) && input.price > 0) {
    parts.push(`€${input.price.toFixed(2)}`)
  }
  const device = formatListingShareDevice(input.manufacturer, input.modelName)
  const displayTitle = (input.title || '').trim()
  if (device && (!displayTitle || !shareTitleContainsDevice(displayTitle, device))) {
    parts.push(device)
  }
  if (input.conditionLabel?.trim()) parts.push(input.conditionLabel.trim())
  return parts.join(' · ')
}

/** Share sheet / clipboard body without URL (pass `url` separately to Web Share API). */
export function buildListingShareBody(
  input: Omit<ListingShareInput, 'url'>,
  brand = LISTING_SHARE_BRAND,
): string {
  const displayTitle = normalizeListingShareTitle(input.title, input.manufacturer)
  const titleLine = buildListingShareTitle(displayTitle, brand, input.manufacturer)
  const details = buildListingShareDescription({ ...input, title: displayTitle })
  return [titleLine, details].filter(Boolean).join('\n')
}

/** Clipboard fallback — single URL at the end. */
export function buildListingShareText(input: ListingShareInput, brand = LISTING_SHARE_BRAND): string {
  const body = buildListingShareBody(input, brand)
  const url = input.url.trim()
  return url ? `${body}\n${url}` : body
}
